// Bridges installed plugins' declared commands into the command palette. Each
// enabled plugin's manifest `contributes.commands` becomes a slash command
// (`/wc:count`), with a bare `/wc` alias when the plugin declares a single
// command. Running one delegates to host.runCommand, which lazily activates the
// plugin and invokes its handler. Without this bridge an installed plugin's
// commands never reach the palette.

import { useMemo, useSyncExternalStore } from 'react';
import type { PluginHost } from '../plugins/PluginHost';
import { commandSlash, bareSlash } from '../plugins/pluginCommandSlash';
import type { Command } from './types';

const NOOP_UNSUBSCRIBE = (): void => {};

/** Map every enabled plugin's declared commands to palette slash `Command`s. */
export function pluginCommands(host: PluginHost): Command[] {
  const out: Command[] = [];
  for (const plugin of host.list()) {
    const { manifest } = plugin;
    if (!host.isEnabled(manifest.id)) continue;
    const cmds = manifest.contributes?.commands ?? [];
    const bare = cmds.length === 1 ? bareSlash(manifest) : undefined;
    for (const c of cmds) {
      out.push({
        id: `plugin.${manifest.id}.${c.id}`,
        name: c.title,
        group: 'Plugins',
        enabled: true,
        slash: commandSlash(manifest, c.id),
        aliases: bare ? [bare] : undefined,
        run: () => {
          void host.runCommand(manifest.id, c.id);
        },
      });
    }
  }
  return out;
}

/** Plugin palette commands, re-derived whenever the host's declarative
 *  contribution set changes (install / uninstall / enable / disable). Returns an
 *  empty list when no host is provided (e.g. in tests that omit the prop). */
export function usePluginCommands(host: PluginHost | null): Command[] {
  const version = useSyncExternalStore(
    (cb) => (host ? host.onDidChangeContributions(cb) : NOOP_UNSUBSCRIBE),
    () => (host ? host.contributionVersion() : 0),
    () => (host ? host.contributionVersion() : 0),
  );
  return useMemo(() => (host ? pluginCommands(host) : []), [host, version]);
}
